import { isFulfilled } from "@reduxjs/toolkit";
import { FC, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { toast } from "react-toastify";
import {
  DEFAULT_ITEMS_PER_PAGE,
  ERROR_MESSAGES,
  responseTimeframes,
} from "../../../constants/app";
import { STORAGE_KEYS } from "../../../constants/storageKeys";
import {
  TSelectCandidateForVacancyPayload,
  VACANCY_CANDIDATE_VISIBILITY
} from "../../../models/vacancy";
import { AUTH_ROUTE } from "../../../pages/App/routes";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import {
  hideTransparentPreloaderAction,
  showNotificationAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import { getCandidateDataForCentreThunk } from "../../../store/centers/thunks";
import { PaginationLink } from "../../../store/superadmin/types";
import {
  getAppliedCandidatesDetailsForVacancy,
  getVacancyById,
  selectCandidateForVacancyThunk,
  toggleCandidatePreferredStatus,
} from "../../../store/vacancy/thunks";
import {
  TToggleCandidatePreferredParams,
  TUserDetails,
  TVacancy,
  TVacancyDetail,
} from "../../../store/vacancy/types";
import storage from "../../../utils/storage";
import PaginationControls from "../../Pagination/PaginationControls";
import CustomButton from "../../UI/Buttons/CustomButton";
import PopupModal from "../../UI/Popup";
import VacancyPosition from "../../UI/VacancyPosition";
import { formatTimeframes } from "../../Vacancy/timeframeApiHelper";
import ViewProfile from "../Candidates/PopupModal/ViewProfile";
import CancelVacancy from "./Popup/CancelVacancy";
import AppliedCandidate from "./View/AppliedCandidate";
import JobOverview from "./View/JobOverview";
import JobShareDetails from "./View/JobShareDetails";
import SelectedCandidateCard from "./View/SelectedCandidateCard";
import VacancyHeader from "./View/VacancyHeader";

type ModalType = "CANDIDATE_PROFILE" | "CANCEL" | null;

const ViewPending: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = location.pathname.includes("admin");

  const getVacancyId = storage.get(STORAGE_KEYS.SELECTED_VACANCY_ID) ?? "";
  const inProgress = useSelector((state) => state.vacancy.inProgress);
  const selectedVacancyDetails = useSelector(
    (state) => state.vacancy.selectedVacancy
  );
  const allAppliedCandidatesLists = useSelector(
    (state) => state.vacancy.allAppliedCandidatesList
  );
  const totalCandidates = allAppliedCandidatesLists?.total ?? 0;

  const formattedTimeframes =
    selectedVacancyDetails?.timeframes &&
    formatTimeframes(
      selectedVacancyDetails !== null ? selectedVacancyDetails?.timeframes : []
    );

  const selectedTimeFrame = responseTimeframes.find((responseTimeframe) =>
    responseTimeframe.value === selectedVacancyDetails?.response_timeframe
      ? responseTimeframe.label
      : ""
  );

  const extendedTimeframes = [
    ...responseTimeframes,
    { label: "4 days", value: 5760 },
    { label: "5 days", value: 7200 },
  ];
  const allSelectedTimeFrame = extendedTimeframes.find(
    (tf) => tf.value === selectedVacancyDetails?.all_response_timeframe
  );

  const [showModal, setShowModal] = useState<boolean>(false);
  const [showProfileModal, setShowProfileModal] = useState<boolean>(false);
  const [selectedCandidateId, setSelectedCandidateId] = useState<number>(0);
  const [selectedCandidateName, setSelectedCandidateName] = useState<
    string | null
  >(null);
  const [selectedVacancyId, setSelectedVacancyId] = useState<number>(0);
  const [openApplicantButton, setOpenApplicantButton] =
    useState<boolean>(false);
  const [viewSelectedCandidateProfile, setViewSelectedCandidateProfile] =
    useState<number | null>(0);
  const openApplicantRef = useRef<HTMLDivElement | null>(null);

  const [currentAppliedCandidatePage, setCurrentAppliedCandidatePage] =
    useState<number>(1);
  const [appliedCandidateTotalPages, setAppliedCandidateTotalPages] =
    useState<number>(1);
  const [appliedCandidatePaginationLinks, setAppliedCandidatePaginationLinks] =
    useState<PaginationLink[]>([]);
  const [appliedCandidatePerPage, setAppliedCandidatePerPage] =
    useState<number>(DEFAULT_ITEMS_PER_PAGE);

  const [activeModal, setActiveModal] = useState<ModalType>(null);
  useEffect(() => {
    handleGetVacancyDetails();
  }, []);

  const handleGetVacancyDetails = async () => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(getVacancyById(getVacancyId as string));
    await dispatch(getAppliedCandidatesList);
    dispatch(hideTransparentPreloaderAction());
  };

  const getAppliedCandidatesList = async () => {
    dispatch(showTransparentPreloaderAction());

    await dispatch(
      getAppliedCandidatesDetailsForVacancy({
        uuid: getVacancyId as string,
        page: currentAppliedCandidatePage,
        per_page: appliedCandidatePerPage,
      })
    );

    dispatch(hideTransparentPreloaderAction());
  };

  /**
   * Updates the current page and items per page when the component mounts or when the `candidatesList` changes.
   */
  useEffect(() => {
    if (allAppliedCandidatesLists && allAppliedCandidatesLists?.total > 0) {
      setAppliedCandidateTotalPages(allAppliedCandidatesLists?.last_page || 1);
      setAppliedCandidatePaginationLinks(allAppliedCandidatesLists?.links);
    }
  }, [allAppliedCandidatesLists]);

  /**
   * Handles the change in the number of items per page by updating the `itemPerPageList` state.
   *
   * @param value - The numeric value representing the number of items per page.
   */
  const handlePageChange = async (url: string | null) => {
    if (url) {
      const page = new URL(url).searchParams.get("page");
      setCurrentAppliedCandidatePage(Number(page));
      await dispatch(
        getAppliedCandidatesDetailsForVacancy({
          uuid: getVacancyId as string,
          page: page,
          per_page: appliedCandidatePerPage,
        })
      );
    }
  };

  const handleEditVacancy = (vacancy: TVacancy | null) => {
    storage.set(STORAGE_KEYS.SELECTED_VACANCY_ID, vacancy?.uuid);
    navigate(isAdmin ? AUTH_ROUTE.ADMIN_EDIT_VACANCY : AUTH_ROUTE.EDIT_VACANCY);
  };

  const handleDuplicateVacancy = (vacancy_uuid: string) => {
    storage.set(STORAGE_KEYS.DUPLICATE_VACANCY_ID, vacancy_uuid);
    navigate(
      isAdmin ? AUTH_ROUTE.ADMIN_CREATE_VACANCY : AUTH_ROUTE.CREATE_VACANCY
    );
  };

  const handleSelect = async (vacancy_id: number, candidate_id: number) => {
    setShowModal(!false);
    setSelectedVacancyId(vacancy_id);
    setSelectedCandidateId(candidate_id);
    const getSelectedCandidateName = allAppliedCandidatesLists?.data?.find(
      (allAppliedCandidatesList: TUserDetails) =>
        allAppliedCandidatesList?.id === candidate_id
    );
    setSelectedCandidateName(
      getSelectedCandidateName?.first_name +
        " " +
        getSelectedCandidateName?.last_name
    );
  };

  const handleViewProfile = async (
    id: string | null,
    centre_id: string | null
  ) => {
    setShowProfileModal(!false);
    setOpenApplicantButton(false);
    dispatch(showTransparentPreloaderAction());
    let centreId: string | null = "";
    if (centre_id) {
      centreId = centre_id;
    } else {
      centreId = storage.get(STORAGE_KEYS.SELECTED_CENTRE_ID);
    }
    await dispatch(
      getCandidateDataForCentreThunk({
        candidate_id: String(id),
        centre_id: centreId,
      })
    );
    dispatch(hideTransparentPreloaderAction());
  };

  const handlePreferredCandidate = async (
    allAppliedCandidateData: TUserDetails,
    action: "remove" | "mark" | null,
    type: boolean
  ) => {
    const payload: TToggleCandidatePreferredParams = {
      centre_id: Number(storage.get(STORAGE_KEYS.SELECTED_CENTRE_ID)),
      centre_uuid: String(storage.get(STORAGE_KEYS.SELECTED_CENTRE_UUID)),
      user_id: allAppliedCandidateData.id ?? 0,
      user_uuid: allAppliedCandidateData.uuid ?? "",
      action: action,
      is_preferred: type,
    };
    const result = await dispatch(toggleCandidatePreferredStatus(payload));
    if (isFulfilled(result)) {
    } else {
      toast.error(
        "Mark Candidate as Preferred failed. Please select the centre and try again."
      );
    }
  };

  const handleConfirmCandidate = async () => {
    const payload: TSelectCandidateForVacancyPayload = {
      vacancy_id: selectedVacancyId,
      candidate_id: selectedCandidateId,
    };
    const res = await dispatch(selectCandidateForVacancyThunk(payload));
    if (isFulfilled(res)) {
      if(res.payload.data.is_timeframe_conflict) {
        dispatch(showNotificationAction(ERROR_MESSAGES.SELECTION_FAILED))
      } else {
        await dispatch(showNotificationAction("Done"));
        await dispatch(getVacancyById(getVacancyId as string));
      }
    }
    setShowModal(false);
  };

  const onClickClose = () => {
    setShowModal(false);
    setShowProfileModal(false);
    setActiveModal(null);
  };

  // Redirect to vacancy list based on user type
  const redirectToVacancies = () => {
    navigate(
      isAdmin ? AUTH_ROUTE.ADMIN_URGENT_VACANCIES : AUTH_ROUTE.CLIENT_VACANCIES
    );
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        openApplicantRef.current &&
        !openApplicantRef.current.contains(event.target as Node)
      ) {
        setOpenApplicantButton(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCancel = () => {
    setActiveModal("CANCEL");
  };

  const {
    status,
    centre,
    uuid: vacancy_uuid,
    selected_candidate,
    vacancy_current_status
  } = selectedVacancyDetails || {};

  const renderCandidateMappedToVacancy = () => {
    return (
      selected_candidate !== null && (
        (selectedVacancyDetails?.visibility_type === VACANCY_CANDIDATE_VISIBILITY.SELECTED_CANDIDATE
          && selectedVacancyDetails.visibility_upgraded === null) ||
        (status !== "active")
      ) && (
        <SelectedCandidateCard
          selectedVacancyDetails={selectedVacancyDetails}
          isAdmin={isAdmin}
          onViewProfile={() =>
            handleViewProfile(
              String(selected_candidate?.uuid),
              String(centre?.id)
            )
          }
        />
      )
    );
  };

  const renderAppliedCandidates = () => {
    return (
      (status === "active" || (vacancy_current_status === "expired" && status !== "filled"))  &&
      totalCandidates > 0 && (
        <>
          <div className="fs-22 fw-400 text-black">
            Applications From {totalCandidates > 0 ? totalCandidates : 0}{" "}
            Candidates
          </div>
          <div className="d-flex gap-20 flex-wrap">
            {allAppliedCandidatesLists?.data?.map(
              (appliedCandidate: TUserDetails, index: number) => (
                <AppliedCandidate
                  key={index}
                  isAdmin={isAdmin}
                  selectedVacancyDetails={selectedVacancyDetails}
                  appliedCandidate={appliedCandidate}
                  openApplicantRef={openApplicantRef}
                  onSelect={(vacancyId: number, appliedCandidateId: number) =>
                    handleSelect(vacancyId, appliedCandidateId)
                  }
                  viewSelectedCandidateProfile={viewSelectedCandidateProfile}
                  handleViewProfile={handleViewProfile}
                  setViewSelectedCandidateProfile={
                    setViewSelectedCandidateProfile
                  }
                  openApplicantButton={openApplicantButton}
                  setOpenApplicantButton={setOpenApplicantButton}
                />
              )
            )}
          </div>
          {allAppliedCandidatesLists &&
            allAppliedCandidatesLists?.total > DEFAULT_ITEMS_PER_PAGE && (
              <PaginationControls
                currentPage={currentAppliedCandidatePage}
                totalPages={appliedCandidateTotalPages}
                paginationLinks={appliedCandidatePaginationLinks}
                onPageChange={handlePageChange}
                setPerPageList={setAppliedCandidatePerPage}
                setCurrentPage={setCurrentAppliedCandidatePage}
              />
            )}
        </>
      )
    );
  };

  return (
    <>
      <ViewProfile
        showProfileModal={showProfileModal}
        onClickClose={onClickClose}
      />
      <div className="d-flex flex-column gap-20">
        <div className="d-flex flex-column gap-20">
          <VacancyHeader
            selectedVacancyDetails={selectedVacancyDetails}
            onBack={redirectToVacancies}
            onDuplicate={() => handleDuplicateVacancy(vacancy_uuid as string)}
            onCancel={handleCancel}
          />
          <JobOverview
            selectedVacancyDetails={selectedVacancyDetails}
            formattedTimeframes={formattedTimeframes}
            onEdit={() => handleEditVacancy(selectedVacancyDetails)}
          />
          {renderCandidateMappedToVacancy()}
        </div>
        <JobShareDetails
          selectedVacancyDetails={selectedVacancyDetails}
          selectedTimeFrame={selectedTimeFrame}
          allSelectedTimeFrame={allSelectedTimeFrame}
        />
        {renderAppliedCandidates()}
        {/* Choose Candidate Modal */}
        <PopupModal
          visible={showModal}
          showCloseBtn={true}
          onClickClose={onClickClose}
          customWidth={650}
        >
          <div className="d-flex flex-column gap-15">
            <div className="fs-22 fw-400 text-black">
              Select {selectedCandidateName}
            </div>
            <div className="fs-13 fw-400 text-black">
              You have chosen {selectedCandidateName} to fill your vacancy:{" "}
              <VacancyPosition
                positions={selectedVacancyDetails?.vacancy_positions}
                alternatePosition={
                  selectedVacancyDetails?.alternate_position_legacy || ""
                }
              />{" "}
              at {selectedVacancyDetails?.centre?.business_name},{" "}
              {selectedVacancyDetails?.centre?.address} on{" "}
              {formattedTimeframes &&
                formattedTimeframes?.map((ftf, i) => (
                  <span
                    key={i}
                    className={`fs-13 ${
                      ftf.alert ? "text-alert fw-700" : "fw-400"
                    }`}
                  >
                    {ftf.label}
                  </span>
                ))}
              {"."}
            </div>
            <div className="d-flex justify-content-end">
              <CustomButton
                variant={"primary"}
                type="button"
                className="btn-theme bg-teal text-black"
                contentClass="d-flex"
                isLoading={inProgress}
                onClick={handleConfirmCandidate}
              >
                Confirm and Notify Candidate
              </CustomButton>
            </div>
          </div>
        </PopupModal>
        {/* Choose Candidate Modal */}
        {/* Cancel Modal */}
        <PopupModal
          visible={!!activeModal}
          showCloseBtn={true}
          onClickClose={onClickClose}
          customWidth={650}
        >
          {/* Cancel Modal */}
          {activeModal === "CANCEL" && selectedVacancyDetails && (
            <CancelVacancy
              vacancy={selectedVacancyDetails as unknown as TVacancyDetail}
              onClose={onClickClose}
            />
          )}
        </PopupModal>
        {/* Cancel Modal */}
      </div>
    </>
  );
};

export default ViewPending;
